
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import FbxButton from '@/components/fbx-button.vue';
import FbxInput from '@/components/fbx-input.vue';
import VirtualRedTagHeader from '@/components/VirtualRedTag/VirtualRedTagHeader.vue';
import { cleanTrailerNumber } from '@/shared/helpers';
import { lookupTrailerById } from '@/shared/trailerLookup';
import checkVirtualRedTagEligibility from '@/shared/mixins/checkVirtualRedTagEligibility';
import { ActionTypes } from '@/store/actionTypes';
import { TrailerSelectionMethod } from '@/types/TrailerSelectionMethod';

const NOT_FOUND_ALERT = 'Not Found!';
const CANT_RED_TAG_ALERT = "Can't Red Tag!";
const UNSUPPORTED_TYPE_HINT =
    'This trailer type is currently not available for Red Tag.';

export default defineComponent({
    name: 'VirtualRedTagManualEntry',
    components: {
        FbxButton,
        FbxInput,
        VirtualRedTagHeader,
    },
    mixins: [checkVirtualRedTagEligibility],
    data: (): {
        trailerNumber: string;
        notFoundAlert: string;
        isUnsupportedType: boolean;
        isLoading: boolean;
    } => {
        return {
            trailerNumber: '',
            notFoundAlert: '',
            isUnsupportedType: false,
            isLoading: false,
        };
    },
    computed: {
        unsupportedTypeHint(): string {
            return UNSUPPORTED_TYPE_HINT;
        },
        ...mapGetters({
            driver: 'getDriver',
        }),
        driverCompany(): string {
            return this.$route.params.company as string;
        },
        canContinue(): boolean {
            // An unsupported type can't be red tagged, so Continue stays off until the
            // driver edits the number.
            return (
                this.trailerNumber.trim().length > 0 &&
                !this.isLoading &&
                !this.isUnsupportedType
            );
        },
    },
    methods: {
        navigateBack(): void {
            this.$router.back();
        },
        onTrailerNumberChanged(value: string): void {
            this.trailerNumber = value ?? '';
            this.notFoundAlert = '';
            this.isUnsupportedType = false;
        },
        async onContinue(): Promise<void> {
            if (!this.canContinue) {
                return;
            }

            this.isLoading = true;
            this.notFoundAlert = '';
            this.isUnsupportedType = false;

            const cleanedId = cleanTrailerNumber(this.trailerNumber);

            // Eligibility (Trailer.TypeClassification) allows Refrigerated trailers
            // into Virtual Red Tag, not just Dry Van.
            const { trailer, isUnsupportedType } = await lookupTrailerById(
                cleanedId,
                this.driverCompany,
                true
            );

            if (trailer) {
                await this.$store.dispatch(ActionTypes.SET_SELECTED_TRAILER, {
                    trailer,
                    trailerSelectionMethod: TrailerSelectionMethod.manual,
                });

                const canRedTag = await this.checkVirtualRedTagEligibility({
                    trailerNumber: trailer.trailerNumber,
                    trailerCompany: trailer.company,
                    driverCompany: this.driverCompany,
                    driverId: this.driver?.id,
                });

                this.isLoading = false;

                // A network/API failure means we never actually got an answer, so it
                // can't be read as "ineligible". Fall back to VIN/plate verification,
                // the same soft degrade the old attach-flow check used for its own
                // failures, rather than dead-ending the driver on a transient blip.
                if (this.virtualRedTagEligibilityCheckFailed) {
                    this.$router.push({
                        name: 'VirtualRedTagVerifyTrailer',
                        params: { trailerNumber: trailer.trailerNumber },
                        query: { trailerCompany: trailer.company },
                    });
                    return;
                }

                if (!canRedTag) {
                    this.$router.push({
                        name: 'VirtualRedTagUnavailable',
                        params: { trailerNumber: trailer.trailerNumber },
                        query: {
                            reason:
                                this.virtualRedTagIneligibilityReason ??
                                undefined,
                            trailerCompany: trailer.company,
                        },
                    });
                    return;
                }

                this.$router.push({
                    name: this.virtualRedTagRequiresIdentityVerification
                        ? 'VirtualRedTagVerifyTrailer'
                        : 'VirtualRedTagConfirmation',
                    params: { trailerNumber: trailer.trailerNumber },
                    // Carried so a refresh re-looks-up the trailer under its own
                    // company rather than the driver's.
                    query: { trailerCompany: trailer.company },
                });
            } else {
                this.isLoading = false;
                // "Not Found!" is reserved for trailers that really don't exist; a
                // trailer that exists but isn't a Dry Van or Refrigerated type gets the
                // "Can't Red Tag!" message (AC3) instead.
                this.isUnsupportedType = isUnsupportedType;
                this.notFoundAlert = isUnsupportedType
                    ? CANT_RED_TAG_ALERT
                    : NOT_FOUND_ALERT;
            }
        },
    },
});
